"use client";

import { useEffect, useRef } from "react";
import { readClientAuthTokenForIntegrationsConnect } from "@/lib/authSession";
import { isGoogleWorkspaceSheetsReconnectPass } from "@/lib/googleIntegrationStatus";
import { fetchGoogleIntegrationStatus } from "@/lib/googleIntegrationStatusFetch";
import { looksLikeLineEmbeddedBrowser } from "@/lib/openLineExternalBrowser";
import {
  SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY,
  consumeSigninSheetsReconnectOAuthDone,
  ensureSigninSheetsReconnectWorkspaceResolved,
  isSigninSheetsReconnectAwaitingOAuth,
  isSigninSheetsReconnectOAuthSuccessMessage,
  navigateSigninSheetsReconnectLineComplete,
} from "@/lib/signinSheetsReconnect";

/**
 * LINE opens Google OAuth in an external browser without `window.opener`.
 * Poll + postMessage + storage events bring the original webview to the success page.
 */
export function useSigninSheetsReconnectOAuthReturn(enabled = true): void {
  const busyRef = useRef(false);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (!looksLikeLineEmbeddedBrowser()) return;

    const tryCompleteFromStatus = async () => {
      if (!isSigninSheetsReconnectAwaitingOAuth()) return;
      if (busyRef.current) return;
      const token = readClientAuthTokenForIntegrationsConnect();
      if (!token) return;

      busyRef.current = true;
      try {
        const search = window.location.search || "";
        const status = await fetchGoogleIntegrationStatus({ force: true });
        const workspaceId = await ensureSigninSheetsReconnectWorkspaceResolved(
          search,
          undefined,
          status,
        );
        if (isGoogleWorkspaceSheetsReconnectPass(status, workspaceId)) {
          navigateSigninSheetsReconnectLineComplete();
        }
      } catch {
        // ignore
      } finally {
        busyRef.current = false;
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (!isSigninSheetsReconnectOAuthSuccessMessage(event.data)) return;
      navigateSigninSheetsReconnectLineComplete();
    };

    const onStorage = (event: StorageEvent) => {
      if (event.key !== SIGNIN_SHEETS_RECONNECT_OAUTH_DONE_KEY || !event.newValue) {
        return;
      }
      navigateSigninSheetsReconnectLineComplete();
    };

    const poll = () => {
      void tryCompleteFromStatus();
    };

    if (consumeSigninSheetsReconnectOAuthDone()) {
      navigateSigninSheetsReconnectLineComplete();
      return;
    }

    window.addEventListener("message", onMessage);
    window.addEventListener("storage", onStorage);
    document.addEventListener("visibilitychange", poll);
    window.addEventListener("focus", poll);
    window.addEventListener("pageshow", poll);
    const intervalId = window.setInterval(poll, 800);
    void tryCompleteFromStatus();

    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", poll);
      window.removeEventListener("focus", poll);
      window.removeEventListener("pageshow", poll);
      window.clearInterval(intervalId);
    };
  }, [enabled]);
}
